import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Eye, TrendingUp, AlertTriangle, Activity, Search, SlidersHorizontal,
  RefreshCw, Clock, Building2, ShieldCheck, FileText, Zap, Briefcase,
  Bell, ChevronRight, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { fmtMoney, DEFAULT_CURRENCY } from "../lib/money";
import EmptyState from "../components/common/EmptyState";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "../components/ui/tooltip";

/**
 * QENVA · Sous Vigilance.
 *
 * Executive surveillance hub — lists every document Guardian is actively
 * watching, with real Guardian Score, ALM owner, open alerts and confidence
 * level for each row. Replaces the legacy Accepted Risk Register.
 *
 * Layout follows the Figma reference (Dashboard V3 brand kit):
 *   • Top utility bar with breadcrumb + "Dernière analyse" pill + "Analyser maintenant" CTA
 *   • Page hero (title + subtitle)
 *   • 4 KPI cards
 *   • Filter pills (All / Critical / Watch / Conform) + search + Filters
 *   • Document table with 7 columns + per-row Guardian badge
 *   • Footer band: total value + "Guardian surveille vos documents en continu"
 */
export default function UnderWatch() {
  const { t, i18n } = useTranslation("under_watch");
  const navigate = useNavigate();
  const locale = (i18n.language || "fr").startsWith("fr") ? "fr" : "en";

  const [bucket, setBucket] = useState("all");
  const [query, setQuery] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchOverview = useCallback(async () => {
    try {
      setRefreshing(true);
      const params = { locale };
      if (bucket !== "all") params.bucket = bucket;
      if (query) params.q = query;
      const r = await api.get("/under-watch/overview", { params });
      setData(r.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur de chargement");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [bucket, query, locale]);

  useEffect(() => { fetchOverview(); }, [fetchOverview]);

  const handleAnalyzeNow = async () => {
    setRefreshing(true);
    try {
      await api.post("/under-watch/analyze-now");
      toast.success(t("toast.analyzed_now"));
      await fetchOverview();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
    } finally {
      setRefreshing(false);
    }
  };

  const counts = data?.bucket_counts || { all: 0, critical: 0, watch: 0, conform: 0 };
  const lastAnalysis = data?.last_analysis_at;

  return (
    <div className="animate-fade-up" data-testid="under-watch-page">
      {/* Top utility bar */}
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <p className="text-[13px] text-zinc-500" data-testid="under-watch-breadcrumb">
          {t("breadcrumb")}
        </p>
        <div className="flex items-center gap-2.5">
          <LastAnalysisBadge isoAt={lastAnalysis} locale={locale} />
          <button
            type="button"
            onClick={handleAnalyzeNow}
            disabled={refreshing}
            data-testid="under-watch-analyze-now"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-qenva-blue text-white font-semibold text-[13px] shadow-sm hover:bg-qenva-blue/90 transition-colors disabled:opacity-60"
          >
            {refreshing ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <RefreshCw size={14} strokeWidth={2} />
            )}
            {t("analyze_now")}
          </button>
        </div>
      </div>

      {/* Page hero */}
      <div className="mb-8 max-w-3xl">
        <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-qenva-blue inline-flex items-center gap-1.5 mb-2">
          <Eye size={12} strokeWidth={1.75} />
          QENVA · {t("brand_tag")}
        </p>
        <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-qenva-navy">
          {t("page_title")}
        </h1>
        <p className="text-[15px] text-zinc-500 mt-2">
          {t("page_subtitle", { count: data?.kpis?.documents_monitored || 0 })}
        </p>
      </div>

      {/* 4 KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-7" data-testid="under-watch-kpis">
        <KpiCard
          icon={<Eye size={18} strokeWidth={1.75} />}
          iconBg="bg-qenva-blue/10" iconColor="text-qenva-blue"
          value={data?.kpis?.documents_monitored ?? 0}
          label={t("kpi.documents_monitored")}
          testId="kpi-documents"
          loading={loading}
        />
        <KpiCard
          icon={<TrendingUp size={18} strokeWidth={1.75} />}
          iconBg="bg-emerald-50" iconColor="text-emerald-600"
          value={fmtMoney(data?.kpis?.value_under_watch_total || 0, data?.kpis?.currency || DEFAULT_CURRENCY)}
          label={t("kpi.value_under_watch")}
          testId="kpi-value"
          loading={loading}
        />
        <KpiCard
          icon={<AlertTriangle size={18} strokeWidth={1.75} />}
          iconBg="bg-rose-50" iconColor="text-rose-500"
          value={data?.kpis?.critical_points ?? 0}
          label={t("kpi.critical_points")}
          testId="kpi-critical"
          loading={loading}
        />
        <KpiCard
          icon={<Activity size={18} strokeWidth={1.75} />}
          iconBg="bg-amber-50" iconColor="text-amber-600"
          value={data?.kpis?.analyses_today ?? 0}
          label={t("kpi.analyses_today")}
          testId="kpi-analyses-today"
          loading={loading}
        />
      </div>

      {/* Filter pills + search */}
      <div className="flex items-center gap-3 flex-wrap mb-5" data-testid="under-watch-filters">
        <FilterPills bucket={bucket} setBucket={setBucket} counts={counts} />
        <div className="ml-auto flex items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("search_placeholder")}
              data-testid="under-watch-search"
              className="pl-9 pr-3 py-2 text-[13px] border border-zinc-200 rounded-full bg-white w-72 outline-none focus:border-qenva-blue/60"
            />
          </div>
          <button
            type="button"
            disabled
            title={t("filters_soon")}
            data-testid="under-watch-filters-btn"
            className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full border border-zinc-200 bg-white text-[13px] text-zinc-500 hover:text-zinc-800"
          >
            <SlidersHorizontal size={13} />
            {t("filters_label")}
          </button>
        </div>
      </div>

      {/* Document table */}
      <DocTable rows={data?.rows || []} loading={loading} locale={locale} currency={data?.kpis?.currency || DEFAULT_CURRENCY} onRowClick={(id) => navigate(`/documents/${id}`)} />

      {/* Footer surveillance band */}
      <SurveillanceBand
        totalValue={data?.kpis?.value_under_watch_total || 0}
        currency={data?.kpis?.currency || DEFAULT_CURRENCY}
        rowCount={data?.total_rows ?? 0}
        monitoredCount={data?.kpis?.documents_monitored ?? 0}
        engagements={data?.surveillance?.active_engagements ?? 0}
        locale={locale}
        onAnalyze={handleAnalyzeNow}
        loading={refreshing}
      />
    </div>
  );
}

/* ---------------------------- subcomponents ---------------------------- */

function KpiCard({ icon, iconBg, iconColor, value, label, testId, loading }) {
  return (
    <div
      className="rounded-2xl border border-zinc-200 bg-white px-5 py-5 flex items-start gap-4"
      data-testid={testId}
    >
      <div className={`w-11 h-11 rounded-2xl grid place-items-center shrink-0 ${iconBg} ${iconColor}`}>
        {icon}
      </div>
      <div className="min-w-0">
        {loading ? (
          <div className="h-7 w-16 bg-zinc-100 rounded animate-pulse" />
        ) : (
          <p className="font-display text-[26px] font-semibold tracking-tight text-qenva-navy leading-none tabular-nums">
            {value}
          </p>
        )}
        <p className="text-[12.5px] text-zinc-500 mt-1.5">{label}</p>
      </div>
    </div>
  );
}

function FilterPills({ bucket, setBucket, counts }) {
  const { t } = useTranslation("under_watch");
  const pills = [
    { key: "all",       label: t("buckets.all"),       count: counts.all,       dot: null },
    { key: "critical",  label: t("buckets.critical"),  count: counts.critical,  dot: "bg-rose-500" },
    { key: "watch",     label: t("buckets.watch"),     count: counts.watch,     dot: "bg-amber-500" },
    { key: "conform",   label: t("buckets.conform"),   count: counts.conform,   dot: "bg-emerald-500" },
  ];
  return (
    <div className="inline-flex items-center gap-2 flex-wrap">
      {pills.map((p) => {
        const active = bucket === p.key;
        return (
          <button
            key={p.key}
            type="button"
            onClick={() => setBucket(p.key)}
            data-testid={`bucket-${p.key}`}
            className={`inline-flex items-center gap-2 pl-3.5 pr-2 py-1.5 rounded-full text-[12.5px] font-semibold border transition-colors ${
              active
                ? "bg-qenva-navy text-white border-qenva-navy"
                : "bg-white text-zinc-700 border-zinc-200 hover:border-qenva-blue/40"
            }`}
          >
            {p.dot && <span className={`w-1.5 h-1.5 rounded-full ${p.dot}`} />}
            <span>{p.label}</span>
            <span
              className={`inline-block min-w-[20px] text-center px-1.5 py-0.5 rounded-full text-[11px] font-bold tabular-nums ${
                active ? "bg-white/15 text-white" : "bg-zinc-100 text-zinc-600"
              }`}
            >
              {p.count}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function LastAnalysisBadge({ isoAt, locale }) {
  const { t } = useTranslation("under_watch");
  const text = useMemo(() => {
    if (!isoAt) return t("last_analysis.never");
    const diff = Date.now() - new Date(isoAt).getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return t("last_analysis.just_now");
    if (minutes < 60) return t("last_analysis.minutes_ago", { count: minutes });
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return t("last_analysis.hours_ago", { count: hours });
    const days = Math.floor(hours / 24);
    return t("last_analysis.days_ago", { count: days });
  }, [isoAt, locale, t]);
  return (
    <span
      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-qenva-blue/5 text-qenva-blue text-[12.5px] font-medium"
      data-testid="last-analysis-badge"
    >
      <Clock size={12} />
      {text}
    </span>
  );
}

const TYPE_ICONS = {
  lease: { icon: Building2, bg: "bg-rose-50", color: "text-rose-500" },
  insurance: { icon: ShieldCheck, bg: "bg-amber-50", color: "text-amber-600" },
  financing: { icon: TrendingUp, bg: "bg-emerald-50", color: "text-emerald-600" },
  contract: { icon: FileText, bg: "bg-emerald-50", color: "text-emerald-600" },
  permit: { icon: FileText, bg: "bg-zinc-100", color: "text-zinc-500" },
  financials: { icon: TrendingUp, bg: "bg-blue-50", color: "text-blue-600" },
  supplier: { icon: Zap, bg: "bg-emerald-50", color: "text-emerald-600" },
  hr: { icon: Briefcase, bg: "bg-amber-50", color: "text-amber-600" },
  tech: { icon: Zap, bg: "bg-emerald-50", color: "text-emerald-600" },
  technology: { icon: Zap, bg: "bg-emerald-50", color: "text-emerald-600" },
};

function DocTable({ rows, loading, locale, currency, onRowClick }) {
  const { t } = useTranslation("under_watch");

  if (loading) {
    return (
      <div className="rounded-2xl border border-zinc-200 bg-white p-12 text-center text-zinc-400 inline-flex items-center justify-center gap-2 w-full">
        <Loader2 className="w-4 h-4 animate-spin" /> {t("loading")}
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <EmptyState
        icon={Eye}
        title={t("empty.title")}
        subtitle={t("empty.subtitle")}
        testId="under-watch-empty"
      />
    );
  }

  return (
    <div
      className="rounded-2xl border border-zinc-200 bg-white overflow-hidden"
      data-testid="under-watch-table"
    >
      {/* Header */}
      <div className="grid grid-cols-12 px-5 py-3 border-b border-zinc-100 bg-zinc-50/60 text-[10.5px] uppercase tracking-[0.14em] font-bold text-zinc-400 gap-3">
        <div className="col-span-3">{t("col.document")}</div>
        <div className="col-span-1">{t("col.type")}</div>
        <div className="col-span-2">{t("col.next_deadline")}</div>
        <div className="col-span-1">{t("col.risk")}</div>
        <div className="col-span-2 text-right">{t("col.value")}</div>
        <div className="col-span-1">{t("col.last_analysis")}</div>
        <div className="col-span-2 text-right">{t("col.guardian")}</div>
      </div>

      {/* Rows */}
      <ul className="divide-y divide-zinc-100">
        {rows.map((row) => (
          <DocRow key={row.id} row={row} locale={locale} currency={currency} onClick={() => onRowClick?.(row.id)} />
        ))}
      </ul>
    </div>
  );
}

function DocRow({ row, locale, currency, onClick }) {
  const { t } = useTranslation("under_watch");
  const typeMeta = TYPE_ICONS[row.document_type] || { icon: FileText, bg: "bg-zinc-100", color: "text-zinc-500" };
  const TypeIcon = typeMeta.icon;
  const isCritical = row.risk_bucket === "critical";
  const isWatch = row.risk_bucket === "watch";

  // Iter62 — contracts with auto-renewal have BOTH a notice_deadline (last day
  // to give notice) and a contract_end_date. When the notice_deadline is within
  // 60 days we visually escalate to amber/orange so the user notices the
  // window is closing. The tooltip explains why this date matters and what
  // happens past it.
  const hasContractNoticeWindow =
    row.document_type === "contract" && row.notice_deadline;
  let noticeInWindow = false;
  if (hasContractNoticeWindow) {
    try {
      const days = Math.ceil(
        (new Date(row.notice_deadline).getTime() - Date.now()) / 86400000,
      );
      noticeInWindow = days >= 0 && days <= 60;
    } catch { /* malformed date — ignore */ }
  }

  const deadlineColor = isCritical
    ? "text-rose-500"
    : (isWatch || noticeInWindow) ? "text-amber-600" : "text-zinc-500";
  const riskColor = isCritical
    ? "bg-rose-50 text-rose-500 border-rose-200"
    : isWatch
      ? "bg-amber-50 text-amber-600 border-amber-200"
      : "bg-emerald-50 text-emerald-600 border-emerald-200";
  const riskDot = isCritical ? "bg-rose-500" : isWatch ? "bg-amber-500" : "bg-emerald-500";

  const scoreColor = row.guardian_score >= 80
    ? "text-emerald-600"
    : row.guardian_score >= 50
      ? "text-amber-600"
      : "text-rose-500";

  return (
    <li
      className="grid grid-cols-12 items-center px-5 py-4 hover:bg-zinc-50/70 cursor-pointer transition-colors gap-3"
      onClick={onClick}
      data-testid={`under-watch-row-${row.id}`}
    >
      {/* Document */}
      <div className="col-span-3 flex items-center gap-3 min-w-0">
        <div className={`w-10 h-10 rounded-2xl grid place-items-center shrink-0 ${typeMeta.bg} ${typeMeta.color}`}>
          <TypeIcon size={16} strokeWidth={1.75} />
        </div>
        <div className="min-w-0">
          <p className="font-display text-[14.5px] font-semibold text-qenva-navy truncate">
            {row.title}
          </p>
          {row.subtitle && (
            <p className="text-[12px] text-zinc-500 truncate">{row.subtitle}</p>
          )}
        </div>
      </div>

      {/* Type chip */}
      <div className="col-span-1">
        <span className="inline-block px-2.5 py-1 rounded-full bg-zinc-100 text-zinc-600 text-[11.5px] font-medium whitespace-nowrap">
          {row.document_type_label}
        </span>
      </div>

      {/* Next deadline — for contracts with auto-renewal, the date shown is
          the notice_deadline (last day to give notice); a tooltip exposes
          the real contract_end_date so the user understands both. */}
      <div className={`col-span-2 text-[13px] font-medium tabular-nums ${deadlineColor} inline-flex items-center gap-1.5`}>
        <Clock size={11} />
        {row.next_deadline ? (
          hasContractNoticeWindow ? (
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    data-testid={`deadline-notice-${row.id}`}
                    className="underline decoration-dotted decoration-zinc-300 underline-offset-2 cursor-help"
                  >
                    {formatDate(row.next_deadline, locale)}
                  </span>
                </TooltipTrigger>
                <TooltipContent
                  side="top"
                  className="max-w-[260px] text-[12px] leading-snug"
                  data-testid={`deadline-tooltip-${row.id}`}
                >
                  {t("notice_tooltip", {
                    contract_end_date: row.contract_end_date
                      ? formatDate(row.contract_end_date, locale)
                      : "—",
                    defaultValue: "Date limite de résiliation — au-delà, le contrat se renouvelle automatiquement jusqu'au {{contract_end_date}}.",
                  })}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            formatDate(row.next_deadline, locale)
          )
        ) : "—"}
      </div>

      {/* Risk pill */}
      <div className="col-span-1">
        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[11.5px] font-semibold ${riskColor}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${riskDot}`} />
          {row.risk_label}
        </span>
      </div>

      {/* Value */}
      <div className="col-span-2 text-right text-[13.5px] font-semibold tabular-nums text-qenva-navy">
        {row.value_under_watch > 0 ? fmtMoney(row.value_under_watch, currency) : "—"}
      </div>

      {/* Last analysis */}
      <div className="col-span-1 text-[12px] text-zinc-500 tabular-nums">
        {relativeTime(row.last_analysis_at, locale, t)}
      </div>

      {/* Guardian: score + alerts + owner + confidence */}
      <div className="col-span-2 flex items-center justify-end gap-2.5 flex-wrap">
        {row.open_alerts_count > 0 && (
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-50 text-rose-600 text-[11px] font-semibold"
            title={t("guardian.open_alerts")}
            data-testid={`row-alerts-${row.id}`}
          >
            <Bell size={10} />
            {row.open_alerts_count}
          </span>
        )}
        <div className="text-right" data-testid={`row-guardian-${row.id}`}>
          <div className="flex items-center gap-1.5 justify-end">
            <span className={`w-1.5 h-1.5 rounded-full ${row.guardian_score >= 80 ? "bg-emerald-500" : row.guardian_score >= 50 ? "bg-amber-500" : "bg-rose-500"}`} />
            <span className={`text-[13px] font-bold tabular-nums ${scoreColor}`}>
              {row.guardian_score}
              <span className="text-zinc-400 font-normal text-[11px]">/100</span>
            </span>
          </div>
          <p className="text-[10px] text-zinc-400 tabular-nums">
            {t("guardian.confidence", { value: row.guardian_confidence })}
          </p>
        </div>
        {row.owner ? (
          <OwnerAvatar owner={row.owner} />
        ) : (
          <span
            className="inline-block w-7 h-7 rounded-full bg-zinc-100 text-zinc-400 grid place-items-center text-[10px] font-bold uppercase"
            title={t("guardian.unassigned")}
          >
            —
          </span>
        )}
        <ChevronRight size={14} className="text-zinc-300" />
      </div>
    </li>
  );
}

function OwnerAvatar({ owner }) {
  const initials = (owner.display_name || owner.email || "?")
    .split(/[\s@]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join("");
  return (
    <span
      className="inline-flex w-7 h-7 rounded-full bg-qenva-navy text-white items-center justify-center text-[10px] font-bold ring-2 ring-white"
      title={owner.display_name || owner.email}
      data-testid={`row-owner-${owner.user_id}`}
    >
      {owner.avatar_url ? (
        <img
          src={owner.avatar_url}
          alt={owner.display_name}
          className="w-full h-full rounded-full object-cover"
        />
      ) : (
        initials || "?"
      )}
    </span>
  );
}

function SurveillanceBand({ totalValue, currency, rowCount, monitoredCount, engagements, locale, onAnalyze, loading }) {
  const { t } = useTranslation("under_watch");
  return (
    <>
      {/* Summary line */}
      <div className="flex items-center justify-between flex-wrap gap-3 mt-2 mb-5 px-1 text-[13px] text-zinc-500">
        <p data-testid="under-watch-row-count">
          {t("footer.row_count", { count: rowCount })}
        </p>
        <p>
          {t("footer.total_value")}{" "}
          <span className="font-display font-bold text-qenva-navy tabular-nums">
            {fmtMoney(totalValue || 0, currency || DEFAULT_CURRENCY)}
          </span>
        </p>
      </div>

      {/* Surveillance hero band */}
      <div
        className="rounded-2xl bg-qenva-navy p-6 sm:p-7 text-white flex items-center justify-between flex-wrap gap-5"
        data-testid="surveillance-band"
      >
        <div className="flex items-center gap-4 min-w-0">
          <div className="w-12 h-12 rounded-2xl bg-white/10 grid place-items-center shrink-0">
            <ShieldCheck size={20} strokeWidth={1.75} className="text-emerald-400" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <p className="font-display text-[18px] font-semibold tracking-tight">
                {t("surveillance.title")}
              </p>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 text-[10.5px] font-bold uppercase tracking-wider">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                {t("surveillance.online")}
              </span>
            </div>
            <p className="text-[12.5px] text-white/70">
              {t("surveillance.subtitle", { count: monitoredCount })}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onAnalyze}
          disabled={loading}
          data-testid="surveillance-analyze-now"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-qenva-blue text-white font-semibold text-[13px] hover:bg-qenva-blue/90 transition-colors disabled:opacity-60"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          {t("analyze_now")}
        </button>
      </div>
    </>
  );
}

/* ----------------------------- helpers ------------------------------- */

function formatDate(iso, locale) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(locale === "en" ? "en-GB" : "fr-FR", {
      day: "2-digit", month: "short", year: "numeric",
    });
  } catch {
    return iso;
  }
}

function relativeTime(iso, locale, t) {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return t("last_analysis.just_now");
  if (minutes < 60) return t("last_analysis.minutes_ago", { count: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t("last_analysis.hours_ago", { count: hours });
  const days = Math.floor(hours / 24);
  return t("last_analysis.days_ago", { count: days });
}
